import { useQuery } from "@rocicorp/zero/react"
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useMatches,
} from "@tanstack/react-router"
import { cx } from "cva"
import * as React from "react"
import * as z from "zod/mini"

import type { Row } from "../../generated/schema.ts"
import * as queries from "../../shared/queries.ts"
import { Menu } from "../components/Menu.tsx"
import * as Auth from "../lib/auth.ts"
import { rpc } from "../lib/rpc.ts"
import { Layout } from "./-components/Layout.tsx"

export const Route = createFileRoute("/$owner/$repo/~")({
  component: LayoutComponent,
  async beforeLoad(opts) {
    const { context, params } = opts
    const data = await context.queryClient.ensureQueryData({
      queryKey: [{ owner: params.owner, repo: params.repo }] as const,
      async queryFn({ queryKey }) {
        const response = await rpc.repositories[":owner"][":repo"].$get({
          param: queryKey[0],
        })
        if (response.status === 404)
          throw {
            owner: await rpc.accounts[":login"]
              .$get({ param: { login: queryKey[0].owner } })
              .then((x) => (x.status === 200 ? x.json() : undefined))
              .catch(() => undefined),
            response,
          } satisfies ErrorComponent.Props["error"]
        return await response.json()
      },
    })
    // follow redirect (RepositoryRedirect)
    if (data.owner.login !== params.owner || data.name !== params.repo)
      throw redirect({
        to:
          (opts.matches.at(-1)?.routeId as
            | "/$owner/$repo/~/$channel"
            | "/$owner/$repo/~/threads") ?? "/$owner/$repo",
        params: { ...params, owner: data.owner.login, repo: data.name },
      })
    return { repository: data, repositoryId: data.id }
  },
  loader(opts) {
    const { context } = opts
    context.zero.preload(
      queries.repositoryWithChannels(context.authData, {
        repositoryId: context.repositoryId,
      }),
    )
    if (context.authData)
      context.zero.preload(
        queries.accountWithRepositorySubscriptions(context.authData, {
          repositoryId: context.repositoryId,
        }),
      )
  },
  errorComponent(props) {
    return <ErrorComponent {...props} />
  },
  validateSearch: z.object({
    nav: z.optional(z.boolean()),
  }),
})

function LayoutComponent() {
  const context = Route.useRouteContext()
  const search = Route.useSearch()

  const { data: authData } = Auth.useAuthData()

  const [repository] = useQuery(
    queries.repositoryWithChannels(authData, {
      repositoryId: context.repositoryId,
    }),
    { ttl: "5m" },
  )
  const repositoryId = React.useMemo(
    () => repository?.id ?? context.repositoryId,
    [repository?.id, context.repositoryId],
  )

  const [account] = useQuery(
    queries.accountWithRepositorySubscriptions(authData, {
      repositoryId,
    }),
    { enabled: Boolean(authData?.id) },
  )

  const repositoryOwner = React.useMemo(() => {
    if (!repository) return context.repository.owner
    if (repository.account)
      return { ...repository.account, type: "account" } as const
    if (repository.organization)
      return { ...repository.organization, type: "organization" } as const
    return context.repository.owner
  }, [context.repository.owner, repository])

  const [repositories] = useQuery(
    queries
      .repositoriesForAccount(authData, {
        id: repositoryOwner.id,
        type: repositoryOwner.type,
      })
      .orderBy("name", "asc")
      .where("name", "!=", repository?.name ?? context.repository.name),
  )

  return (
    <Layout direction="horizontal">
      <Layout.StartSidebar visibility={search.nav ? "visible" : "hidden"}>
        {repository && (
          <Sidebar
            account={account}
            channelSubscriptions={account?.channel_subscriptions}
            channels={repository.channels}
            repositories={repositories}
            repository={repository}
            repositoryOwner={repositoryOwner}
            threadSubscriptions={account?.thread_subscriptions}
          />
        )}
      </Layout.StartSidebar>
      <Outlet />
    </Layout>
  )
}

function Sidebar(props: {
  account: Pick<Row.account, "avatar_url" | "login"> | undefined
  channels:
    | readonly (Pick<Row.channel, "id" | "name" | "private"> & {
        // name is plural, but this is supposed to be the last message sent in the channel
        messages?: Pick<Row.message, "inserted_at"> | undefined
      })[]
    | undefined
  channelSubscriptions:
    | readonly Pick<
        Row.channel_subscription,
        "channel_id" | "last_read_at" | "subscribed_at"
      >[]
    | undefined
  repository: Pick<Row.repository, "name" | "private">
  repositories: Pick<Row.repository, "id" | "name" | "private">[] | undefined
  repositoryOwner: Pick<Row.account, "avatar_url" | "id" | "login">
  threadSubscriptions:
    | readonly (Pick<
        Row.thread_subscription,
        "last_read_at" | "subscribed_at"
      > & { thread: Pick<Row.thread, "last_message_at"> | undefined })[]
    | undefined
}) {
  const {
    account,
    channels = [],
    channelSubscriptions = [],
    repository,
    repositories = [],
    repositoryOwner,
    threadSubscriptions = [],
  } = props

  const [isChannelUnread, channelsWithUnreadStatus] = React.useMemo(() => {
    const channelIdSubscriptionMap = new Map<
      string,
      Pick<
        Row.channel_subscription,
        "channel_id" | "last_read_at" | "subscribed_at"
      >
    >()
    for (const subscription of channelSubscriptions)
      channelIdSubscriptionMap.set(subscription.channel_id, subscription)
    let isChannelUnread = false
    const results = []
    for (const channel of channels) {
      const subscription = channelIdSubscriptionMap.get(channel.id)
      const lastMessageAt = channel.messages?.inserted_at
      const isUnread =
        subscription?.subscribed_at &&
        lastMessageAt &&
        lastMessageAt > subscription.last_read_at
      results.push({ ...channel, isUnread })
      if (isUnread) isChannelUnread = true
    }
    return [isChannelUnread, results] as const
  }, [channels, channelSubscriptions])

  const isThreadUnread = React.useMemo(() => {
    return threadSubscriptions.some((subscription) => {
      const lastMessageAt = subscription.thread?.last_message_at
      return Boolean(
        subscription.subscribed_at &&
          lastMessageAt &&
          lastMessageAt > subscription.last_read_at,
      )
    })
  }, [threadSubscriptions])

  const matches = useMatches()
  const params = React.useMemo(
    () => ({
      owner: repositoryOwner.login,
      repo: repository.name,
    }),
    [repository, repositoryOwner],
  )

  // update title for unread messages
  React.useEffect(() => {
    const title = document.title.replace("* ", "")
    if (isChannelUnread || isThreadUnread) document.title = `* ${title}`
    else document.title = title
  }, [isChannelUnread, isThreadUnread])

  return (
    <div className="flex h-full w-full flex-col">
      <div className="flex h-12 items-center justify-between px-3.5">
        <div className="flex items-center gap-1.5">
          <Link
            className="flex items-center gap-1 rounded font-bold text-[14px]"
            params={{ owner: repositoryOwner.login }}
            to="/$owner"
          >
            <img
              alt={repositoryOwner.login}
              className="size-7 select-none rounded-md text-transparent shadow-px"
              src={repositoryOwner.avatar_url}
            />
          </Link>

          <div />

          <Link
            className="flex items-center gap-1 rounded font-bold text-[14px]"
            params={params}
            to="/$owner/$repo"
          >
            {repository.name}
            {repository.private && <OcticonLock16 className="size-3" />}
          </Link>
        </div>

        <Link
          className="hidden text-muted hover:text-default max-md:block"
          params={(p) => ({ ...p, ...params })}
          search={(x) => ({ ...x, nav: undefined })}
          to={
            (matches.at(-1)?.routeId as
              | "/$owner/$repo/~/$channel"
              | "/$owner/$repo/~/threads") ?? "/$owner/$repo"
          }
        >
          <OcticonX16 className="size-5" />
        </Link>
      </div>

      <nav className="scrollbar-neutral-6 dark:scrollbar-neutral-7 scrollbar-width-thin flex w-full flex-1 flex-col gap-4.5 overflow-y-auto overflow-x-hidden pt-3">
        {account && (
          <ul className="w-full space-y-px px-2 text-[14px]">
            <li>
              <Link
                activeProps={{
                  className: "bg-neutral-3 dark:bg-neutral-2 text-default",
                }}
                className={cx(
                  "relative flex h-7.5 w-full items-center rounded-md px-2 hover:bg-neutral-3/80 hover:text-default dark:hover:bg-neutral-2/60",
                  isThreadUnread && "font-bold text-default!",
                )}
                inactiveProps={{ className: "text-neutral-10" }}
                params={params}
                to="/$owner/$repo/~/threads"
              >
                <div className="flex items-center gap-2">
                  <GitchatThread className="size-4 text-muted" />
                  Threads
                </div>
              </Link>
            </li>
          </ul>
        )}

        {channelsWithUnreadStatus.length > 0 && (
          <div className="flex flex-col gap-1">
            <h2 className="px-3.5 font-medium text-[12px] text-muted">
              Channels
            </h2>
            <ul className="w-full space-y-px px-2 text-[14px]">
              {channelsWithUnreadStatus.map((channel) => {
                return (
                  <li key={channel.id}>
                    <Link
                      activeProps={{
                        className:
                          "bg-neutral-3 dark:bg-neutral-2 text-default",
                      }}
                      className={cx(
                        "relative flex h-7.5 w-full items-center rounded-md px-2 hover:bg-neutral-3/80 hover:text-default dark:hover:bg-neutral-2/60",
                        channel.isUnread && "font-bold text-default!",
                      )}
                      inactiveProps={{ className: "text-neutral-10" }}
                      params={{ ...params, channel: channel.name }}
                      to="/$owner/$repo/~/$channel"
                    >
                      <div className="flex items-center gap-2">
                        {channel.private ? (
                          <OcticonLock16 className="mt-px size-4 text-muted" />
                        ) : (
                          <OcticonHash16 className="mt-px size-4 text-muted" />
                        )}
                        {channel.name}
                      </div>
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>
        )}

        {repositories.length > 0 && (
          <div className="flex flex-col gap-1">
            <h2 className="px-3.5 font-medium text-[12px] text-muted">
              Related Repositories
            </h2>
            <ul className="w-full space-y-px px-2 text-[14px]">
              {repositories.map((repository) => (
                <li key={repository.id}>
                  <Link
                    className="relative flex h-7.5 w-full items-center rounded-md px-2 text-neutral-10 hover:bg-neutral-3/80 hover:text-default dark:hover:bg-neutral-2/60"
                    params={{ ...params, repo: repository.name }}
                    to="/$owner/$repo"
                  >
                    <div className="flex items-center gap-2">
                      {repository.private ? (
                        <OcticonRepoLocked16 className="mt-px size-4 text-muted" />
                      ) : (
                        <OcticonRepo16 className="mt-px size-4 text-muted" />
                      )}
                      {repository.name}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex-1" />

        {account && (
          <div className="flex min-h-12.5 items-center gap-1.5 px-3.5">
            <Menu>
              <Menu.Button>
                <img
                  alt={account.login}
                  className="size-7 rounded-full text-transparent shadow-px"
                  src={account.avatar_url}
                />
              </Menu.Button>
              <Menu.Menu>
                <Menu.Item
                  icon={OcticonSignOut16}
                  onClick={() => Auth.logout()}
                >
                  Sign out
                </Menu.Item>
              </Menu.Menu>
            </Menu>
          </div>
        )}
      </nav>
    </div>
  )
}

function ErrorComponent(props: ErrorComponent.Props) {
  const { error } = props

  const { owner, repo } = Route.useParams()

  if ("response" in error)
    return (
      <Layout direction="vertical">
        <Header repositoryOwner={error?.owner} />

        <Layout.Content>
          {error.response?.status === 404 ? (
            <Layout.Empty title="Repository not found">
              The repository{" "}
              <code>
                {owner}/{repo}
              </code>{" "}
              does not exist or has not been set up with GitChat yet.
            </Layout.Empty>
          ) : (
            <Layout.Empty title="An error occurred">
              {error.message ??
                "An error occurred while loading the repository."}
            </Layout.Empty>
          )}
        </Layout.Content>
      </Layout>
    )

  return (
    <Layout direction="vertical">
      <Layout.Content>
        <Layout.Empty title="Something went wrong">
          {error.message}
        </Layout.Empty>
      </Layout.Content>
    </Layout>
  )
}

declare namespace ErrorComponent {
  type Props = {
    error:
      | Error
      | {
          message?: string | undefined
          owner?:
            | (Pick<Row.account, "avatar_url" | "id" | "login"> & {
                type: "account" | "organization"
              })
            | undefined
          response?: Response
        }
  }
}

function Header(props: {
  repository?: Layout.Breadcrumb.Props["repository"] | undefined
  repositoryOwner?: Layout.Breadcrumb.Props["repositoryOwner"] | undefined
}) {
  const { repository, repositoryOwner } = props

  return (
    <Layout.Header showLogo={!repositoryOwner}>
      {repositoryOwner && (
        <Layout.Breadcrumb
          repository={repository}
          repositoryOwner={repositoryOwner}
        />
      )}
    </Layout.Header>
  )
}
